import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Loader2, CheckCircle2, ShieldCheck, MailCheck } from "lucide-react";
import { subscribeLead, persistUtm, LOGO_URL } from "@/lib/checkout";
import { trackEvent, trackLead } from "@/lib/analytics";
import SiteFooter from "@/components/SiteFooter";

const BENEFITS = [
  "One page you can print tonight, no app or account needed",
  "Start with a single priority instead of a 30 item list",
  "A brain dump box so nothing important slips away",
  "Honest time blocks and a gentle reset for the hard days",
];

export default function FreePlanner() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const [msg, setMsg] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    persistUtm();
    trackEvent("page_view", { page: "free_planner" });
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus("error");
      setMsg("Please enter a valid email address.");
      return;
    }
    setStatus("loading");
    setMsg("");
    try {
      const res = await subscribeLead(email, { variant: "X", source: "free_planner_lp" });
      setStatus("done");
      trackLead({ source: "free_planner_lp" });
      if (res.download_url) window.open(res.download_url, "_blank", "noopener");
    } catch (err) {
      setStatus("error");
      setMsg("Something went wrong. Please try again.");
    }
  };

  const firstName = email.split("@")[0];

  return (
    <div className="min-h-screen bg-[#F7F5F0] text-[#1F2430]">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-lg focus:bg-[#0B132B] focus:px-4 focus:py-2 focus:text-white">
        Skip to main content
      </a>

      <header className="border-b border-[#E6E9EE] bg-white">
        <div className="mx-auto flex max-w-[1000px] items-center justify-between px-6 py-4">
          <Link to="/" aria-label="CommandCenterADHD home">
            <img src={LOGO_URL} alt="CommandCenterADHD" width="640" height="316" className="h-9 w-auto object-contain" />
          </Link>
          <span className="font-heading text-sm font-bold text-[#15816B]">Free daily planner</span>
        </div>
      </header>

      <main id="main">
        <section className="px-6 pt-14 pb-12 md:pt-20" aria-labelledby="fp-heading">
          <div className="mx-auto max-w-[760px] text-center">
            <h1 id="fp-heading" className="font-heading text-4xl font-extrabold leading-tight tracking-tight text-[#0B132B] sm:text-5xl">
              The free ADHD daily planner that survives a bad day
            </h1>
            <p className="mx-auto mt-5 max-w-[560px] text-lg leading-relaxed text-[#3A4252]">
              The ADHD daily planner is a free one page printable that gives your day a calm shape, one priority, a few quick wins, and honest time blocks. It is built for the way ADHD planning actually goes. Enter your email and we send it straight to your inbox, no account required.
            </p>
            <p className="mx-auto mt-3 max-w-[560px] text-[15px] leading-relaxed text-[#3A4252]">
              Not sure where to begin today? Try the free{" "}
              <Link to="/what-should-i-do-next" className="font-semibold text-[#15816B] underline underline-offset-2 hover:text-[#0B6b57]">What should I do next tool</Link>.
            </p>

            {status === "done" ? (
              <div
                className="mx-auto mt-10 max-w-[480px] rounded-2xl border border-[#15816B]/30 bg-white p-7 text-center shadow-[0_16px_40px_-24px_rgba(11,19,43,0.35)]"
                data-testid="freeplanner-success"
                role="status"
                aria-live="polite"
              >
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#15816B]/12">
                  <CheckCircle2 className="h-8 w-8 text-[#15816B]" aria-hidden="true" />
                </div>
                <h2 className="mt-4 font-heading text-2xl font-bold text-[#0B132B]">
                  {firstName ? `On its way, ${firstName}!` : "Check your inbox!"}
                </h2>
                <p className="mx-auto mt-2 max-w-[380px] text-[15px] leading-relaxed text-[#3A4252]">
                  Your planner just opened in a new tab and a copy is heading to your email. If you do not see it in a few minutes, check spam or promotions.
                </p>
                <Link
                  to="/get-started"
                  className="mt-6 inline-flex items-center justify-center rounded-xl bg-[#0B132B] px-6 py-3 font-heading text-sm font-bold text-white transition-transform duration-200 hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#15816B] focus-visible:ring-offset-2"
                  data-testid="freeplanner-upgrade-link"
                >
                  See the full Command Center
                </Link>
              </div>
            ) : (
              <form onSubmit={submit} noValidate className="mx-auto mt-8 max-w-[440px] text-left">
                <label htmlFor="fp-email" className="block text-sm font-semibold text-[#0B132B]">
                  Your email
                </label>
                <input
                  ref={inputRef}
                  id="fp-email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder="name@email.com"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); if (status === "error") setStatus("idle"); }}
                  aria-invalid={status === "error"}
                  aria-describedby={status === "error" ? "fp-email-error" : "fp-email-help"}
                  data-testid="freeplanner-email-input"
                  className="mt-1.5 h-12 w-full rounded-xl border border-[#C9D0D9] bg-white px-4 text-base text-[#0B132B] outline-none focus-visible:border-[#15816B] focus-visible:ring-2 focus-visible:ring-[#15816B]/40"
                />
                {status === "error" ? (
                  <p id="fp-email-error" role="alert" className="mt-2 text-sm font-medium text-[#B02A1E]" data-testid="freeplanner-error">{msg}</p>
                ) : (
                  <p id="fp-email-help" className="mt-2 text-sm text-[#5A6472]">We email your planner here. Unsubscribe anytime.</p>
                )}
                <button
                  type="submit"
                  disabled={status === "loading"}
                  data-testid="freeplanner-submit-button"
                  className="mt-4 inline-flex h-14 w-full items-center justify-center gap-2 rounded-xl bg-[#15816B] px-6 py-3.5 font-heading text-base font-bold text-white transition-transform duration-200 hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0B132B] focus-visible:ring-offset-2 disabled:opacity-70"
                >
                  {status === "loading" ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> : "Send my free planner"}
                </button>
                <p className="mt-3 flex items-center justify-center gap-2 text-xs font-medium text-[#5A6472]">
                  <ShieldCheck className="h-4 w-4 text-[#15816B]" aria-hidden="true" />
                  No spam. Your email is only used to send the planner and occasional tips.
                </p>
              </form>
            )}
          </div>
        </section>

        <section className="px-6 pb-16" aria-labelledby="fp-what-heading">
          <div className="mx-auto max-w-[900px] overflow-hidden rounded-2xl border border-[#E6E9EE] bg-white p-6 shadow-[0_16px_40px_-24px_rgba(11,19,43,0.35)] md:grid md:grid-cols-2 md:gap-8 md:p-8">
            <div>
              <h2 id="fp-what-heading" className="font-heading text-2xl font-bold text-[#0B132B]">What is inside</h2>
              <ul className="mt-5 space-y-3">
                {BENEFITS.map((b) => (
                  <li key={b} className="flex gap-3 text-[15px] leading-relaxed text-[#3A4252]">
                    <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#15816B]" aria-hidden="true" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-5 flex items-center gap-2 text-sm font-medium text-[#3A4252]">
                <MailCheck className="h-4 w-4 text-[#15816B]" aria-hidden="true" />
                Delivered instantly by email.
              </p>
            </div>
            <div className="mt-6 overflow-hidden rounded-xl border border-[#EEF1F5] bg-[#0B132B] md:mt-0" style={{ aspectRatio: "16 / 9" }}>
              <img
                src="/proof-brain-dump.jpg"
                alt="The CommandCenterADHD daily planner showing a brain dump box and a single priority"
                loading="lazy"
                className="h-full w-full object-cover object-top"
              />
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
